import { useEffect, useRef, useState } from 'react';
import { NJIconButton } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { PLAN, UMR } from '../proposition-c/tokens';
import type { Collaborator } from './types';

/** Rouge FDS pour l'action destructive du menu (aucun équivalent dans les tokens UMR mesurés). */
const DANGER = 'var(--nj-core-color-red-700, #b2201c)';

/** Pastille d'initiales — même densité que les avatars du chrome UrbanMap. */
export function Avatar({ person, size = 20, owner = false }: { person: Collaborator; size?: number; owner?: boolean }) {
  return (
    <span
      title={owner ? `${person.name} — créateur du projet` : person.name}
      style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        background: owner ? PLAN.accent : UMR.blue, color: UMR.white,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 700, letterSpacing: 0.2,
      }}
    >
      {person.initials}
    </span>
  );
}

/** Créateur du projet + état de partage (nombre de personnes avec qui il est partagé). */
export function OwnerLine({ owner, members }: { owner: Collaborator; members: Collaborator[] }) {
  const shared = members.length > 0;
  return (
    <div style={{ marginTop: 6, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '16px', color: UMR.textSecondary, minWidth: 0 }}>
      <Avatar person={owner} owner />
      <span title={`Créé par ${owner.name}`} style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {owner.name}
      </span>
      <span
        title={shared ? `Partagé avec ${members.map((m) => m.name).join(', ')}` : 'Projet non partagé — vous seul y avez accès'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 3, padding: '0 5px', flexShrink: 0,
          border: `1px solid ${shared ? PLAN.accentBorder : UMR.border}`, background: shared ? PLAN.accentBg : UMR.white,
          color: shared ? PLAN.accentStrong : UMR.textSecondary, fontSize: 11, lineHeight: '16px',
        }}
      >
        <UMIcon name={shared ? 'group' : 'lock'} size={12} color={shared ? PLAN.accent : UMR.textSecondary} />
        <span style={{ whiteSpace: 'nowrap' }}>{shared ? `+${members.length}` : 'Privé'}</span>
      </span>
    </div>
  );
}

export interface MenuItem {
  icon: string;
  label: string;
  danger?: boolean;
  onSelect: () => void;
}

/** Menu « … » de la carte : actions secondaires (dupliquer, archiver, supprimer). */
export function CardMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={root} style={{ position: 'relative', display: 'inline-flex' }}>
      <NJIconButton
        icon="more_vert"
        label={label}
        variant="tertiary"
        scale="xs"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      />
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', right: 0, bottom: 'calc(100% + 4px)', zIndex: 30, minWidth: 180,
            background: UMR.white, border: `1px solid ${UMR.border}`, boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className="um-hover-grey100"
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', border: 'none',
                background: 'transparent', cursor: 'pointer', fontFamily: UMR.font, fontSize: 13, lineHeight: '20px',
                color: item.danger ? DANGER : UMR.textStrong, textAlign: 'left',
              }}
            >
              <UMIcon name={item.icon} size={16} color={item.danger ? DANGER : UMR.textSecondary} />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
